/**
 * First-time "How to play" content for every live game mode (Cole,
 * 2026-09-25: "all modes should provide a first time tutorial before
 * engaging"). A beta tester accepted a Poker challenge, was dropped straight
 * into the round, and had no idea how to play; exiting would have forfeited.
 *
 * Pure data, no React Native imports, so node tests can check the copy.
 * Customer copy: American English, no em dashes, "gate" never "arch", no
 * "Bluetooth", nothing about how the gate senses the ball.
 *
 * Keep each mode's steps in line with its mode-card "How it works" text in
 * SoloChallengeHost.tsx. When a rule changes, change both.
 */

export type ModeTutorialId =
  | 'target'
  | 'distance'
  | 'sim9'
  | 'startLine'
  | 'ladder'
  | 'ladderDuel'
  | 'poker'
  | 'lineFour'
  | 'turfWar'
  | 'baseball'
  | 'football'
  | 'cricket'
  | 'koth';

export interface ModeTutorialStep {
  heading: string;
  body: string;
}

export interface ModeTutorial {
  id: ModeTutorialId;
  /** The mode's name as the Challenge tab shows it. */
  title: string;
  /** One line under the title: what the game is. */
  summary: string;
  steps: readonly ModeTutorialStep[];
}

export const MODE_TUTORIALS: Record<ModeTutorialId, ModeTutorial> = {
  target: {
    id: 'target',
    title: 'Target',
    summary: 'Free practice at the distance you choose.',
    steps: [
      { heading: 'Pick a distance', body: 'Set how far away the hole is. Change it any time.' },
      { heading: 'Wait for READY', body: 'When the status pill turns green, roll your putt through the gate.' },
      { heading: 'Read the result', body: 'See where the putt would finish: how long or short, and how far off line. No score, just reps.' },
    ],
  },
  distance: {
    id: 'distance',
    title: 'Distance Challenge',
    summary: 'Five putts at one distance. Closest to the hole wins.',
    steps: [
      { heading: 'Five putts, one distance', body: 'Every putt in the round is from the same distance.' },
      { heading: 'Your score', body: 'Your average distance from the hole across the five putts. Lower is better.' },
      { heading: 'Ways to play', body: "Play for your best, take today's official run (one a day), pass the phone around in local play, or send the round to a friend to beat." },
    ],
  },
  sim9: {
    id: 'sim9',
    title: 'Simulated 9',
    summary: 'Nine holes on a 3D course, with real putts.',
    steps: [
      { heading: 'Putt until it drops', body: 'Each hole is a set distance. Keep putting from where the ball stops until it goes in.' },
      { heading: 'Gimmes', body: 'Leave a putt inside 5 ft and the next one is given to you.' },
      { heading: 'Count your strokes', body: 'Your score is your total strokes against par. Tap ROUND for the scorecard.' },
      { heading: 'Ways to play', body: 'Play solo, with up to 6 players on one phone, or challenge a friend who races your round putt for putt.' },
    ],
  },
  startLine: {
    id: 'startLine',
    title: 'Start Line',
    summary: 'Two ways to train your start line.',
    steps: [
      { heading: 'Streak', body: 'Pick a distance from 4 to 12 ft and hole as many in a row as you can. One miss ends the run.' },
      { heading: 'Line and pace', body: 'A make needs both. Hit it too firm and only a dead-straight putt drops, so a long streak means a pure start line at the right speed.' },
      { heading: 'Practice', body: 'No cup and no target. Every putt is graded on how straight it starts.' },
      { heading: 'The grades', body: 'PURE would still hole from 20 ft. SLIGHT would hole from 10 ft. Both keep your practice streak going. WIDE or MAJOR resets it.' },
    ],
  },
  ladder: {
    id: 'ladder',
    title: 'Putting Ladder',
    summary: 'Climb by rolling each putt farther than the last.',
    steps: [
      { heading: 'Open at 10 feet', body: 'Every ladder runs from 10 to 50 feet.' },
      { heading: 'Climb', body: 'Every putt has to roll farther than the one before it.' },
      { heading: 'The run ends', body: 'when a putt comes up short of the last one, or when you reach 50 feet.' },
    ],
  },
  ladderDuel: {
    id: 'ladderDuel',
    title: 'Putting Ladder 1v1',
    summary: 'Take turns climbing. Bust and they take the pot.',
    steps: [
      { heading: 'Take turns climbing', body: "From 10 to 50 feet, every putt must roll farther than the last one, yours or your opponent's." },
      { heading: 'Bust and they bank it', body: 'Come up short or roll past 50, and your opponent banks a point for every climb this round, yours included.' },
      { heading: 'Hit 50', body: 'Land 50 feet on the number and you win the round outright. Play to any score you like.' },
    ],
  },
  poker: {
    id: 'poker',
    title: 'Poker',
    summary: 'Five holes. Earn cards with every putt. Best hand wins.',
    steps: [
      { heading: 'Same five holes', body: 'Both players putt the same five distances, from 15 to 50 ft.' },
      { heading: 'Earn cards', body: 'Hole it for 3 cards. Finish inside the inner ring for 2, or the outer ring for 1. The rings grow with the hole: 2 ft on a 15-footer, 6 ft on a 50.' },
      { heading: 'One shared deck', body: "Every card you take is a card your opponent can't get. Hands are open between holes." },
      { heading: 'Best hand wins', body: 'After five holes the app picks your best five cards. A short hand still plays: a pair off three cards beats any high card.' },
    ],
  },
  lineFour: {
    id: 'lineFour',
    title: 'Line Four',
    summary: 'Putt pieces onto the board. Four in a row wins.',
    steps: [
      { heading: 'Seven columns', body: 'Seven labeled bands on the green, A to G, match the columns on the board.' },
      { heading: 'Your putt drops a piece', body: 'The piece lands in the column where your ball stops. Missing the board (15 to 50 ft) or a full column spends your turn.' },
      { heading: 'Four in a row wins', body: 'Across, up, or on a diagonal.' },
    ],
  },
  turfWar: {
    id: 'turfWar',
    title: 'Turf War',
    summary: 'Red versus blue. Control the most zones.',
    steps: [
      { heading: 'Five zones', body: 'Zones run from 10 to 60 ft. Players alternate, ten putts each.' },
      { heading: 'Control a zone', body: 'Whoever has more balls in a zone controls it. A tie makes it neutral.' },
      { heading: 'Most zones wins', body: 'Tied after regulation? Both players putt at one neutral zone. The first time exactly one ball lands there, that player wins.' },
    ],
  },
  baseball: {
    id: 'baseball',
    title: 'Baseball',
    summary: 'Four holes, three outs, three innings.',
    steps: [
      { heading: 'Four holes', body: '15 ft is a single, 25 a double, 35 a triple, 45 a home run. Stop inside a hole’s 3 ft ring for that hit. Each hit tightens when you earn it: 1.5 ft, then hole it, then it is used up. Earn all four hits to reset every ring to 3 ft. Miss and you are out.' },
      { heading: 'The fielder answers', body: 'The fielder putts at the same hole. Getting inside the batter’s ball turns the hit into an out. A holed hit cannot be answered, and it moves every runner one extra base.' },
      { heading: 'Play the situation', body: 'Runners on and under two outs: hole the 5 ft bunt to move them up. With a runner on first, a miss near the single or short of the double is a double-play chance. With a runner on third, just outside the triple or home run ring is a sacrifice fly: the run scores unless the fielder finishes inside 1.5 ft (runner holds) or holes it (double play).' },
      { heading: 'Three innings', body: 'Three outs a side, the away team bats first. Tied at the end: one at-bat each, or another inning.' },
    ],
  },
  football: {
    id: 'football',
    title: 'Football',
    summary: 'Your putt is the play call. Score more than the other side.',
    steps: [
      { heading: 'Yards are where your ball stops', body: 'Five cups: inside run 9 ft, outside run 18, short pass 27, medium pass 36, deep pass 45. Stop inside a cup’s ring and you gain the yards to where your ball stopped (3 ft is a yard). Hole it for double.' },
      { heading: 'The defense putts back', body: 'From the same cup: finish inside your ball and the play is broken up. Miss every ring and the defense can hole that cup for a fumble or an interception.' },
      { heading: 'Mix your plays', body: 'Like Baseball, each play tightens when it gains: 3 ft, 1.5 ft, hole it, then gone. Gain with all five and they all reset.' },
      { heading: 'Possessions', body: 'Four downs to gain 10. Each side gets 3 possessions (2 in Quick), alternating, and every drive plays to its end. A touchdown is 7; on 4th down go for it, kick or punt (no punting inside the opponent 40). A kick inside its ring can be blocked: the defense holes the same cup. Once the trailing side cannot catch up, it can play it out or end the game.' },
    ],
  },
  cricket: {
    id: 'cricket',
    title: 'Darts: Cricket',
    summary: 'The putt line is a dartboard. Close 15 to 20 and the bull.',
    steps: [
      { heading: 'Numbers are 3 ft bands', body: 'Each number is a 3 ft band of distance: 15 at 20 ft, 16 at 25, 17 at 30, 18 at 35, 19 at 40, 20 at 45. The bull is a circle at 55 ft. Stop in a band to hit its number; the gaps between bands are misses.' },
      { heading: 'Your start line is the multiplier', body: 'Within 0.3° is a triple (the green lane in the middle of each band), within 0.8° a double (yellow), anything wider a single (red). The green outer bull is 1 mark and the red inner bull 2.' },
      { heading: 'Open, score, close', body: 'Three putts a turn. Three marks open a number for you; extra marks score its value (the bull 25) while any opponent has it open. When everyone has it, it is closed.' },
      { heading: 'Win', body: 'Close all seven with at least as many points as everyone else. Standard is 15 rounds and Quick 8: when they run out, the most points win, then the most marks.' },
    ],
  },
  koth: {
    id: 'koth',
    title: 'King of the Hill',
    summary: 'Five holes a month. Best round holds the crown.',
    steps: [
      { heading: 'One course a month', body: 'Five holes of random distances, the same for everyone all month.' },
      { heading: 'One putt per hole', body: 'Your score is your total distance from the cup across the five putts. Lower is better.' },
      { heading: 'One attempt a day', body: "Your best round of the month is your standing. Beat the King's round to take the crown." },
    ],
  },
};

export const MODE_TUTORIAL_IDS = Object.keys(MODE_TUTORIALS) as ModeTutorialId[];

/** Show the tutorial automatically only the first time a mode is opened. */
export function shouldAutoShowTutorial(
  seen: Partial<Record<ModeTutorialId, boolean>> | null | undefined,
  id: ModeTutorialId,
): boolean {
  return seen?.[id] !== true;
}
